import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from "@mui/material";
import JBWDateLabel from "./JBWDateLabel";
import { updateAgeementMatchLocation } from "../services/agreementMatchDataService";
import { AuthContext } from "../AuthContext";
import STLocationSearch from "./STLocationSearch.jsx";
import { getStInstalledEquipmentsByJbwAgreementId } from "../services/agreementServices";

// Sentinels for the per-unit dropdown. KEEP is the default, so a save where nobody touches
// the equipment sends no equipmentMap at all and behaves exactly as it did before.
const KEEP = "__keep__";
const REMOVE = "__remove__";

const STLocationSearchPopup = ({
  dialogOpen = false,
  agreement,
  onClose,
  onGridRefresh,
}) => {
  //const [open, setOpen] = React.useState(dialogOpen);
  const { user } = useContext(AuthContext);
  // const [selectedAgreement, setSelectedAgreement] = useState(null);
  // if (agreement) {
  //   setSelectedAgreement(agreement);
  // }
  const [searchType, setSearchType] = useState("address");
  const [searchValue, setSearchValue] = useState(null);
  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
    setSearchValue(null);
  };

  const [savingData, setSavingData] = useState(false);

  // Equipment mapping. ServiceTitan installed equipment belongs to a location and cannot be
  // moved, so re-pointing the agreement means choosing the records that already exist under
  // the new location. CS asked for this on 2026-08-17 -- the location edit "doesn't transfer
  // anything for equipment or touch the equipment automations" -- and asked to be able to see
  // it, which is why the pairing is chosen here rather than guessed from serial numbers.
  const [currentEquipment, setCurrentEquipment] = useState([]);
  const [newLocationEquipment, setNewLocationEquipment] = useState([]);
  const [equipmentChoices, setEquipmentChoices] = useState({});
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [equipmentError, setEquipmentError] = useState("");

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "left",
    color: theme.palette.text.secondary,
  }));

  // useEffect(() => {
  //   if (agreement) {
  //     setSelectedAgreement(agreement);
  //   }
  // }, [agreement]);

  // Two calls on purpose: asking with the OLD location returns the agreement's units as they
  // stand, asking with the NEW one returns that location's units as candidates. Asking once
  // with the new location would merge the two by invoiceItemId and blur which is which.
  useEffect(() => {
    if (!searchValue || !agreement?.agreementId) {
      setCurrentEquipment([]);
      setNewLocationEquipment([]);
      setEquipmentChoices({});
      return;
    }
    let cancelled = false;
    setLoadingEquipment(true);
    setEquipmentError("");
    Promise.all([
      getStInstalledEquipmentsByJbwAgreementId(
        agreement.agreementId,
        agreement.tenantId,
        agreement.locationId
      ),
      // The candidate call reads the TARGET tenant's location, but the agreement row still
      // lives in the tenant it is moving off -- so that one goes as the owner tenant. Without
      // it a cross-tenant move resolves the agreement in the target tenant, where no such row
      // exists, 404s, and takes the whole Promise.all down with it: no picker, no mapping, and
      // the un-remapped-equipment warning on every DelAir move.
      getStInstalledEquipmentsByJbwAgreementId(
        agreement.agreementId,
        searchValue.tenantId,
        searchValue.locationId,
        agreement.tenantId
      ),
    ])
      .then(([currentRes, newRes]) => {
        if (cancelled) return;
        const onAgreement = (currentRes.data || []).filter(
          (eq) => eq.equipmentType !== "new"
        );
        const atNewLocation = (newRes.data || []).filter(
          (eq) => eq.equipmentType === "new"
        );
        setCurrentEquipment(onAgreement);
        setNewLocationEquipment(atNewLocation);
        setEquipmentChoices(
          Object.fromEntries(
            onAgreement.map((eq) => [String(eq.installedEquipmentId), KEEP])
          )
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.log("Error fetching equipment for the location move:", err);
        setEquipmentError(
          "Could not load equipment from ServiceTitan. You can still save the location; equipment will be left as it is."
        );
        setCurrentEquipment([]);
        setNewLocationEquipment([]);
        setEquipmentChoices({});
      })
      .finally(() => {
        if (!cancelled) setLoadingEquipment(false);
      });
    return () => {
      cancelled = true;
    };
  // Depends on the ids, not the objects: the parent re-renders `agreement` as a fresh
  // object each time, and depending on the reference would re-fire this and wipe the
  // user's choices mid-mapping.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchValue?.locationId,
    searchValue?.tenantId,
    agreement?.agreementId,
    agreement?.tenantId,
    agreement?.locationId,
  ]);

  // Only units the user actually changed go in the map. "to: null" removes the unit from the
  // agreement; anything left on Keep is omitted entirely so its ServiceTitan tags stay put.
  //
  // The chosen unit's specs travel with its id. The re-mapped row keeps dataReceivedFromST = 1
  // (a 0 there stalls the agreement -- jbw_st_dataTransformation.sql:192,210), and that flag is
  // exactly what excludes the row from JbRepository.getLocationIdsForTenant /
  // getAgreementIdsForEquipmentUpdate, which select dataReceivedFromST = 0. The other refresh
  // path, jbwgetstinstalledequipments, merges by invoiceItemId -- still the OLD location's
  // invoice item. So nothing in the system would ever correct these three columns, and the
  // claims and agreement-detail screens would show the OLD unit's make/model/serial under the
  // NEW unit's id indefinitely. Omitted when the candidate has no value: the server keeps
  // whatever the row already holds rather than blanking it.
  const buildEquipmentMap = () =>
    Object.entries(equipmentChoices)
      .filter(([, choice]) => choice !== KEEP)
      .map(([from, choice]) => {
        if (choice === REMOVE) return { from, to: null };
        // newLocationEquipment rows are the `equipmentType: 'new'` shape built by
        // jbwgetstinstalledequipments -- manufacturer / modelNo / serialNumber, keyed by
        // installedEquipmentId (the ServiceTitan record id).
        const target = newLocationEquipment.find(
          (eq) => String(eq.installedEquipmentId) === choice
        );
        return {
          from,
          to: choice,
          manufacturer: target?.manufacturer,
          modelNo: target?.modelNo,
          serialNumber: target?.serialNumber,
        };
      });

  // Mirrors the server-side refusal: getAgreementDetails inner-joins equipment in practice, so
  // an agreement with no units left drops out of the re-push and never reaches the new location.
  const removesEveryUnit =
    currentEquipment.length > 0 &&
    currentEquipment.every(
      (eq) => equipmentChoices[String(eq.installedEquipmentId)] === REMOVE
    );

  const handleEquipmentChoice = (installedEquipmentId, choice) =>
    setEquipmentChoices((previous) => ({
      ...previous,
      [String(installedEquipmentId)]: choice,
    }));

  const handleSaveLocation = async () => {
    setSavingData(true);
    const locationData = {
      locationId: searchValue.locationId,
      tenantId: searchValue.tenantId,
      customerName: searchValue.locationName,
      street: searchValue.street,
      unit: searchValue.unit,
      city: searchValue.city,
      state: searchValue.state,
      zip: searchValue.zip,
      modifiedBy: user.name,
      agreementId: agreement?.agreementId,
      equipmentMap: buildEquipmentMap(),
    };
    const response = await updateAgeementMatchLocation(
      agreement?.id,
      locationData
    );
    console.log("Update Response", response);
    if (response.data) {
      setSearchValue(null);
      setSearchType("address");
      resetEquipment();
      setSavingData(false);
      onGridRefresh();
    } else {
      setSavingData(false);
    }
  };

  const resetEquipment = () => {
    setCurrentEquipment([]);
    setNewLocationEquipment([]);
    setEquipmentChoices({});
    setEquipmentError("");
  };

  const handleLocationSelected = (location) => {
    console.log("location ", location);
    setSearchValue(location);
  };

  const handleClose = (event, reason) => {
    if (reason === "escapeKeyDown" || reason === "backdropClick") {
      return;
    }
    setSearchType("address");
    setSearchValue(null);
    resetEquipment();
    setSavingData(false);
    onClose();
    //setOpen(false);
  };

  const jbwAgreementsTable = (agreement) => {
    return (
      <>
        {agreement ? (
          <TableContainer>
            <Table
              sx={{ minWidth: 550 }}
              size="small"
              aria-label="JBW Agreements table"
            >
              <TableHead sx={{ fontSize: "10px" }}>
                <TableRow>
                  <TableCell>Agreement Id</TableCell>
                  <TableCell align="left">Customer Name</TableCell>
                  <TableCell align="left">Phone & Email</TableCell>
                  <TableCell align="left">Address</TableCell>
                  <TableCell align="left">Purchase Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  key={agreement.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell component="th" scope="row">
                    {agreement?.agreementId}
                  </TableCell>
                  <TableCell align="left">
                    {agreement?.jbwCustomerName}
                  </TableCell>
                  <TableCell align="left">
                    <Typography
                      variant="subtitl1"
                      component="span"
                      sx={{
                        wordWrap: "break-word",
                      }}
                      whiteSpace="normal"
                    >
                      {agreement?.jbwPhoneNumber}
                    </Typography>
                    <br />
                    <Typography
                      variant="subtitl1"
                      component="span"
                      sx={{
                        wordWrap: "break-word",
                      }}
                      whiteSpace="normal"
                    >
                      {!agreement?.jbwEmail || agreement?.jbwEmail !== "null" ? agreement?.jbwEmail : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography
                      variant="subtitl1"
                      component="span"
                      sx={{
                        wordWrap: "break-word",
                      }}
                      whiteSpace="normal"
                    >
                      {agreement.jbwAddressLine1
                        ? `${agreement.jbwAddressLine1},`
                        : ""}
                      {!agreement?.jbwAddressLine2 || agreement?.jbwAddressLine2 !== "null"
                        ? `${agreement?.jbwAddressLine2},`
                        : ""}
                      {agreement?.jbwCity ? `${agreement?.jbwCity},` : ""}
                      {agreement?.jbwState ? `${agreement?.jbwState},` : ""}
                      {agreement?.jbwZip ? `${agreement?.jbwZip}` : ""}.
                    </Typography>
                  </TableCell>

                  <TableCell align="left">
                    {agreement?.jbwPurchaseDate && (
                      <JBWDateLabel
                        inputDate={agreement?.jbwPurchaseDate}
                        dateFormat="MM/DD/YYYY"
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="subtitle1"
            component="h2"
            sx={{ fontWeight: "bold" }}
          >
            No JBW Agreement Details :
          </Typography>
        )}
      </>
    );
  };

  const equipmentLabel = (equipment) =>
    [equipment?.manufacturer, equipment?.modelNo, equipment?.serialNumber]
      .filter((part) => part && part !== "null")
      .join(" · ") || "Unnamed unit";

  const equipmentMappingTable = () => {
    return (
      <TableContainer>
        <Table
          sx={{ minWidth: 550 }}
          size="small"
          aria-label="Equipment mapping table"
        >
          <TableHead sx={{ fontSize: "10px" }}>
            <TableRow>
              <TableCell>Current Equipment</TableCell>
              <TableCell align="left">Installed Equipment Id</TableCell>
              <TableCell align="left">Equipment At New Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentEquipment.map((equipment) => (
              <TableRow
                key={equipment.installedEquipmentId}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell component="th" scope="row">
                  <Typography
                    variant="subtitl1"
                    component="span"
                    sx={{
                      wordWrap: "break-word",
                    }}
                    whiteSpace="normal"
                  >
                    {equipmentLabel(equipment)}
                  </Typography>
                </TableCell>
                <TableCell align="left">
                  {equipment.installedEquipmentId}
                </TableCell>
                <TableCell align="left">
                  <Select
                    size="small"
                    fullWidth
                    value={
                      equipmentChoices[String(equipment.installedEquipmentId)] ||
                      KEEP
                    }
                    onChange={(event) =>
                      handleEquipmentChoice(
                        equipment.installedEquipmentId,
                        event.target.value
                      )
                    }
                  >
                    <MenuItem value={KEEP}>Leave on current equipment</MenuItem>
                    <MenuItem value={REMOVE}>Remove from agreement</MenuItem>
                    {newLocationEquipment.map((candidate) => (
                      <MenuItem
                        key={candidate.installedEquipmentId}
                        value={String(candidate.installedEquipmentId)}
                        disabled={Object.entries(equipmentChoices).some(
                          ([from, choice]) =>
                            choice === String(candidate.installedEquipmentId) &&
                            from !== String(equipment.installedEquipmentId)
                        )}
                      >
                        {equipmentLabel(candidate)}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        disableEscapeKeyDown
        PaperProps={{
          component: "div",
        }}
        fullWidth={true}
        maxWidth={"lg"}
      >
        <DialogTitle>Map Location</DialogTitle>
        {savingData ? (
          <DialogContent>
            <DialogContentText>Saving Location ...</DialogContentText>
          </DialogContent>
        ) : (
          <>
            <DialogContent>
              <Stack spacing={2}>
                <Item>
                  <Stack>
                    <Item elevation={0}>
                      <Typography
                        variant="subtitle1"
                        component="h2"
                        sx={{ fontWeight: "bold" }}
                      >
                        JBW Agreement Details:
                      </Typography>
                    </Item>
                    <Item elevation={0}>{jbwAgreementsTable(agreement)}</Item>
                  </Stack>
                </Item>
                <Item elevation={0}>
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      name="controlled-radio-buttons-group"
                      value={searchType}
                      onChange={handleSearchTypeChange}
                    >
                      <FormControlLabel
                        value="address"
                        control={<Radio />}
                        label="Location Address"
                      />
                      <FormControlLabel
                        value="locationid"
                        control={<Radio />}
                        label="Location ID"
                      />
                    </RadioGroup>
                  </FormControl>
                </Item>
                <Item elevation={0}>
                  <STLocationSearch
                    dealer={agreement?.dealerId}
                    type={searchType}
                    searchValue={searchValue}
                    onLocationSelected={handleLocationSelected}
                  />
                </Item>
                {searchValue && (
                  <Item>
                    <Stack>
                      <Item elevation={0}>
                        <Typography
                          variant="subtitle1"
                          component="h2"
                          sx={{ fontWeight: "bold" }}
                        >
                          Equipment:
                        </Typography>
                      </Item>
                      <Item elevation={0}>
                        {loadingEquipment ? (
                          <Typography variant="subtitle1" component="span">
                            Loading equipment from ServiceTitan ...
                          </Typography>
                        ) : equipmentError ? (
                          <Alert severity="warning">{equipmentError}</Alert>
                        ) : currentEquipment.length === 0 ? (
                          <Typography variant="subtitle1" component="span">
                            No equipment on this agreement.
                          </Typography>
                        ) : (
                          <>
                            {newLocationEquipment.length === 0 && (
                              <Alert severity="info" sx={{ mb: 1 }}>
                                The new location has no installed equipment in
                                ServiceTitan. Leave the units as they are, or
                                remove them from the agreement.
                              </Alert>
                            )}
                            {removesEveryUnit && (
                              <Alert severity="error" sx={{ mb: 1 }}>
                                At least one unit has to stay on the agreement,
                                otherwise it will not be sent to the new
                                location at all.
                              </Alert>
                            )}
                            {equipmentMappingTable()}
                          </>
                        )}
                      </Item>
                    </Stack>
                  </Item>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                disabled={!searchValue || loadingEquipment || removesEveryUnit}
                onClick={handleSaveLocation}
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default STLocationSearchPopup;
